import React, { useState, useCallback, useEffect } from "react";
import CommonFormLabel from "../../components/formLabel";
import Input from "../../components/input";
import ValidationFeedback from "../../components/validationFeedback";
import CommonButton from "../../components/commonButton";
import FileInput from "../../components/fileInput";
import { useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import { Card, Toast, Form, Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import {
  updatePage,
  getPageDetails,
} from "./pageSlice";
import { pageUpdateSchema } from "../../schema/validationSchemas";
import Editor from "../../components/commonEditor";

export default function Edit() {
  const { id: pageId } = useParams();
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const dispatch = useDispatch();

  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const selected_organization_id = pathSegments.length > 1 && pathSegments[0] === "organization" 
    ? pathSegments[1] 
    : null;

  const [page, setPage] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newImagePreview, setNewImagePreview] = useState(null);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dispatch(getPageDetails(pageId));
      setPage(result.payload.data);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching page:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, pageId, setLoading]);

  useEffect(() => {
    setPage(null);
    if (pageId) {
      fetchPage();
    }
  }, [pageId, fetchPage]);

  const handleCancel = () => {
    setPage(null);
    navigate(`/organization/${selected_organization_id}/pages`);
  };

  const handleImageChange = (file, setFieldValue) => {
    if (file) {
      setNewImagePreview(URL.createObjectURL(file));
      setFieldValue("banner_image", file);
    }
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    const updatedValues = { ...values, id: pageId };

    const formData = new FormData();
    formData.append("id", updatedValues.id);
    formData.append("organization_id", selected_organization_id);
    formData.append("_method", "PUT");
    formData.append("title", updatedValues.title);
    formData.append("description", updatedValues.description);
    formData.append("banner_image", updatedValues.banner_image);

    await dispatch(updatePage(formData))
      .then((res) => {
        if (res.payload && res.payload.success) {
          localStorage.setItem("page_notify_message", res.payload.message);
          setSnackbarMessage(res.payload.message);
          setSnackbarVariant("success");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
          setPage(null);
          navigate(`/organization/${selected_organization_id}/pages`);
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(
            res.payload.message || "Failed to update page!"
          );
          setSnackbarVariant("danger");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        setSnackbarMessage("Failed to update page!");
        setSnackbarVariant("danger");
        setShowSnackbar(true);
        setSubmitting(false);
        setIsSubmitting(false);
      });
  };

  if (!page)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <h2>Loading Page Details...</h2>
      </div>
    );

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>

      {page && (
        <Formik
          initialValues={{
            title: page.title || "",
            description: page.description || "",
            banner_image: "",
          }}
          validationSchema={pageUpdateSchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            handleChange,
            handleSubmit,
            values,
            setFieldValue,
          }) => (
            <Form
              onSubmit={handleSubmit}
              encType="multipart/form-data"
              className="bg-white p-4 rounded-2 shadow-sm"
            >
              <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
                <h4 className="mb-4 d-flex justify-content-center">Basic</h4>
                <Row>
                  <Col md={12} className="mb-4">
                    <CommonFormLabel
                      htmlFor="title"
                      title="Page Title"
                    />
                    <Input
                      id="title"
                      name="title"
                      type="text"
                      placeholder="Enter Page Title"
                      onChange={handleChange}
                      value={values.title}
                      className={`shadow-none ${
                        errors?.title && touched.title
                          ? "error"
                          : "correct"
                      }`}
                    />
                    {touched.title && errors?.title && (
                      <ValidationFeedback title={errors?.title} />
                    )}
                  </Col>

                  <div className="mb-4">
                    <CommonFormLabel
                      htmlFor="description"
                      title="Description"
                    />
                    <Editor
                      value={values.description}
                      onChange={(content) =>
                        setFieldValue("description", content)
                      }
                    />
                    {touched.description &&
                      errors?.description && (
                        <ValidationFeedback
                          title={errors?.description}
                        />
                      )}
                  </div>
                </Row>
              </Card>

              <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
                <h4 className="mb-4 d-flex justify-content-center">Image</h4>
                <div className="mb-4">
                  <CommonFormLabel
                    htmlFor="banner_image"
                    title="Upload New Image"
                  />
                  <FileInput
                    name="banner_image"
                    onChange={(file) => handleImageChange(file, setFieldValue)}
                    error={errors?.banner_image}
                    touched={touched.banner_image}
                  />
                  {touched.banner_image && errors?.banner_image && (
                    <ValidationFeedback title={errors?.banner_image} />
                  )}
                </div>

                <Row>
                  {newImagePreview && (
                    <Col md={6} className="">
                      <CommonFormLabel
                        htmlFor="new_selected_image"
                        title="New selected Image"
                      />
                      <div>
                        <img
                          src={newImagePreview}
                          alt="New Selected"
                          className="img-fluid rounded border"
                          style={{ width: "200px", height: "auto" }}
                        />
                      </div>
                    </Col>
                  )}

                  <Col md={6} className="">
                    <CommonFormLabel
                      htmlFor="banner_image"
                      title="Uploaded Image"
                    />
                    <div className="mt-2">
                      <img
                        src={page.banner_image}
                        alt="PageImage"
                        className="img-fluid rounded border"
                        style={{ width: "200px", height: "auto" }}
                      />
                    </div>
                  </Col>
                </Row>
              </Card>
              <hr className="my-5" />

              <div className="d-flex align-items-center justify-content-end gap-3 common-popup-btn">
                <CommonButton
                  title={isSubmitting ? "Updating..." : "Update"}
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary primary-shadow"
                />
                <CommonButton
                  title="Cancel"
                  onClick={handleCancel}
                  className="btn-muted"
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
}
